// Phase HR-003 Wave 4 — Worker Efficiency Study API client.
import apiClient from '@/shared/api/client'

export type LossReasonCode =
  | 'EXCESS_BREAKAGE'
  | 'SHORTAGE_OF_HELPER'
  | 'MATERIAL_DELAY'
  | 'MACHINE_DOWNTIME'
  | 'QUALITY_REJECT'
  | 'SUPERVISOR_INSTRUCTION'
  | 'TRAINING_REFRESHER_NEEDED'
  | 'OTHER'

export interface EligibleWorker {
  employee_id: string
  emp_code: string | null
  emp_name: string | null
  machine_id: string | null
  machine_code: string | null
  machine_section: string | null
  required_role: string
  section_code: string | null
  allocation_id: string
  suggested_standard_kg: number | null
  // The capture page renders existing values as placeholders so
  // supervisor sees what was saved earlier.
  existing_output_id: string | null
  existing_actual_kg: number | null
  existing_standard_kg: number | null
  existing_efficiency_pct: number | null
  existing_loss_reason_code: LossReasonCode | null
  existing_action_taken: string | null
  existing_downtime_minutes: number | null
}

export interface WorkerOutputRow {
  employee_id: string
  machine_id?: string | null
  section_code?: string | null
  count_id?: string | null
  allocation_id?: string | null
  standard_target_kg: number
  actual_output_kg: number
  downtime_minutes?: number
  loss_reason_code?: LossReasonCode | null
  loss_reason_text?: string | null
  action_taken?: string | null
}

export interface WorkerOutputBulk {
  output_date: string  // YYYY-MM-DD
  shift_id: string
  plant_id: string
  department_id: string
  rows: WorkerOutputRow[]
}

export interface WorkerOutput {
  id: string
  output_date: string
  shift_id: string
  plant_id: string
  department_id: string
  employee_id: string
  employee_name: string | null
  emp_code: string | null
  machine_id: string | null
  machine_code: string | null
  section_code: string | null
  count_id: string | null
  allocation_id: string | null
  standard_target_kg: string
  actual_output_kg: string
  efficiency_pct: string
  downtime_minutes: string
  loss_reason_code: LossReasonCode | null
  loss_reason_text: string | null
  action_taken: string | null
  captured_by: string | null
  captured_at: string
  verified_by: string | null
  verified_at: string | null
  created_at: string
}

export interface WorkerOutputStats {
  output_date: string
  shift_id: string | null
  total_workers: number
  avg_efficiency_pct: string | null
  below_85_count: number
  below_85_pct: string | null
  total_target_kg: string
  total_actual_kg: string
  total_downtime_minutes: string
  by_loss_reason: Record<string, number>
}

const BASE = '/hr/worker-efficiency'

export const workerEfficiencyApi = {
  eligible: async (params: {
    output_date: string; shift_id: string; department_id: string
  }) => (await apiClient.get<EligibleWorker[]>(`${BASE}/eligible`, { params })).data,

  list: async (params: {
    output_date: string; shift_id: string; department_id?: string
  }) => (await apiClient.get<WorkerOutput[]>(BASE, { params })).data,

  employeeTrend: async (employee_id: string, params?: {
    from_date?: string; to_date?: string
  }) => (await apiClient.get<WorkerOutput[]>(
    `${BASE}/employee/${employee_id}`, { params })).data,

  bulkSave: async (body: WorkerOutputBulk) =>
    (await apiClient.post<WorkerOutput[]>(`${BASE}/bulk`, body)).data,

  verify: async (id: string) =>
    (await apiClient.post<WorkerOutput>(`${BASE}/${id}/verify`)).data,

  stats: async (params: {
    output_date: string; shift_id?: string; department_id?: string
  }) => (await apiClient.get<WorkerOutputStats>(`${BASE}/stats`, { params })).data,
}
